import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography, IconButton, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import { customerService } from '../../services/customer.service';
import { Customer } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

const CustomerList: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try { const data = await customerService.getAll(); setCustomers(data); }
    catch (error) { console.error('Error loading customers:', error); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (selectedCustomer?.id) {
      try { await customerService.delete(selectedCustomer.id); setDeleteDialogOpen(false); loadCustomers(); }
      catch (error) { console.error('Error deleting customer:', error); }
    }
  };

  const columns: GridColDef[] = [
    { field: 'code', headerName: t('common.code'), width: 120 },
    { field: 'name', headerName: t('customers.customer_name'), width: 200 },
    { field: 'contactPerson', headerName: t('common.contact_person'), width: 150 },
    { field: 'email', headerName: t('common.email'), width: 200 },
    { field: 'phone', headerName: t('common.phone'), width: 150 },
    { field: 'city', headerName: t('common.city'), width: 120 },
    { field: 'customerType', headerName: t('common.type'), width: 110 },
    {
      field: 'isActive', headerName: t('common.status'), width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value ? t('common.active') : t('common.inactive')} color={params.value ? 'success' : 'default'} size="small" />
      ),
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small" onClick={() => navigate(`/customers/edit/${params.row.id}`)}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => { setSelectedCustomer(params.row); setDeleteDialogOpen(true); }}><Delete fontSize="small" /></IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('customers.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/customers/new')}>{t('customers.new')}</Button>
      </Box>
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid rows={customers} columns={columns} loading={loading} pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Paper>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('customers.delete_confirm', { name: selectedCustomer?.name || '' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerList;
